import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import {
  ThemeProvider,
  alpha,
  createTheme,
  darken,
  lighten,
} from "@mui/material/styles";
import { useThemeMode } from "./store/theme-mode";
import { __APP_ROOT } from ".";
import colors from 'material-colors';

export const CustomMuiTheme = ({ children, shadowRoot }) => {
  // const fontSize_ = 16 * (16 / parseInt(window.getComputedStyle(document.documentElement).fontSize));
  // const wpFontSize_ = 16 * (16 / parseInt(window.getComputedStyle(document.documentElement).fontSize));
  let borderRadius_ = 4;
  if ( document.querySelector('.btn-customprocess') ) {
    let siteStyles = window.getComputedStyle(document.querySelector('.btn-customprocess'));
    borderRadius_ = parseInt( siteStyles.borderRadius ) === 0 ? borderRadius_ : siteStyles.borderRadius;
  }
// console.log(shadowRoot.firstElementChild);
  const fontSize_ = 16;
  const baseZ = 2147483645;

  const zIndex = {
    modal: baseZ,
    tooltip: baseZ + 1,
    drawer: baseZ + 2,
    swipeableDrawer: baseZ + 3,
    spinner: baseZ + 4,
    splashScreen: 1,
    canvasTools: 2,
  };

  const { mode } = useThemeMode();
  const theme = createTheme({
    palette: {
      mode: mode,
      borderRadius: borderRadius_,
      toast: {
        info: colors.blue[600],
        error: colors.red[500],
        warning: '#ffcb00',
        success: colors.green[700],
      },
    },
    typography: {
      fontFamily: window.getComputedStyle(__APP_ROOT).fontFamily,
      fontWeight: 400,
      fontSize: fontSize_,
      htmlFontSize: fontSize_
    },
    zIndex: zIndex,
    lum(hex) {
      var r = parseInt(hex.substr(1, 2), 16),
          g = parseInt(hex.substr(3, 2), 16),
          b = parseInt(hex.substr(5, 2), 16);
      return 0.2126*r + 0.7152*g + 0.0722*b;;
    },
    components: {
      Spinner: {
        styleOverrides: {
          root: ({theme}) => ({
            color: theme.palette.primary.main,
            zIndex: theme.zIndex.spinner,
          })
        }
      },
      SplashScreen: {
        styleOverrides: {
          root: ({theme}) => ({
            zIndex: theme.zIndex.splashScreen,
          })
        }
      },
      ToggleIconButton: {
        styleOverrides: {
          root: {
            'svg': {
              pointerEvents: 'none',
            }
          }
        }
      },
      ContentEditable: {
        styleOverrides: {
          container: {
            fontSize: fontSize_,
          },
          content: {
            textAlign: 'right'
          }
        }
      },
      MuiToggleFab: {
        styleOverrides: {
          root: ({ theme, active }) => ({
            fontSize: fontSize_,
            backgroundColor: active ? theme.palette.primary.dark : theme.palette.background.paper,
            '&:hover': {
              backgroundColor: active ? darken(theme.palette.primary.dark, 0.1) : darken(theme.palette.background.paper, 0.1),
            },
          })
        }
      },
      MuiFab: {
        defaultProps: {
          size: "small"
        },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0.1))',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: darken(theme.palette.background.paper, 0.1),
            },

          })
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.grey[800]
          }),
          '.MuiSvgIcon': {
            pointerEvents: 'none'
          }
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '1em'
          }),
        },
      },
      MuiSwipeableDrawer: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiButtonBase-root": {
              fontSize: "1.25em",
            },
            "& .MuiButtonBase-root > svg": {
              // color: theme.palette.getContrastText(theme.palette.background.default),
              color: "inherit",
            },
          }),
        },
      },
      CropperToolbar: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiButtonBase-root": {
              fontSize: "1.15em",
              color: theme.palette.text.primary,
            },
            "& .MuiButtonBase-root > svg": {
              // color: theme.palette.getContrastText(theme.palette.background.default),
              color: "inherit",
            },
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: 'inherit'
          })
        }
      },
      MuiPopper: {
        defaultProps: {
          container: function() {
            // console.log(shadowRoot.querySelector('#cp-main-modal'));
            return shadowRoot.firstElementChild;
            // return shadowRoot.querySelector('#cp-main-modal');
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          disableInteractive: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            zIndex: theme.zIndex.tooltip,
          }),
          tooltip: ({ theme }) => ({
            zIndex: theme.zIndex.tooltip,
            padding: theme.spacing(1),
            lineHeight: 1.4,
            backgroundColor: alpha(theme.palette.common.black, 0.8),
            color: theme.palette.common.white,
            boxShadow: theme.shadows[5]
          }),
          popper: {
            fontSize: '16px',
          }
        }
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& [class*=MyToggleButton-root]": {
              borderRadius: "50%",
              padding: theme.spacing(1),
              border: "none",
            },
            "& [class*=-lastButton]": {
              marginLeft: theme.spacing(0.5),
            },
            "& .MuiToggleButtonGroup-grouped.Mui-selected+.MuiToggleButtonGroup-grouped.Mui-selected":
              {
                marginLeft: theme.spacing(0.5),
              },
          }),
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: "xl",
        },
      },
      MuiDrawer: {
        defaultProps: {
          container: shadowRoot.firstElementChild,
        },
      },
      MuiModal: {
        defaultProps: {
          container: shadowRoot.firstElementChild,
        },
      },
      MuiMenu: {
        defaultProps: {
          container: shadowRoot.firstElementChild,
        },
      },
      BizLogo: {
        styleOverrides: {
          image: ({theme}) => ({
            height: 60,
          })
        }
      },
      MuiAppBar: {
        defaultProps: {
          color: 'background',
          position: 'static',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            // backgroundColor: theme.palette.background.default,
            // color: theme.palette.getContrastText(
            //   theme.palette.background.default
            // ),
            boxShadow: "none",
          }),
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: "8px",
            "& ul > li": {
              listStyle: "none",
            },
            "& ul": {
              margin: "0",
              padding: "0",
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            marginBottom: "8px",
            padding: "8px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            marginBottom: "8px",
            paddingLeft: "8px",
            paddingRight: "8px",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            margin: "8px",
          },
        },
      },
      MuiDialog: {
        variants: [
          {
            props: { variant: "warning" },
            style: {
              '& .MuiPaper-root': {
                backgroundColor: '#ffcb00',
              },
              '& .MuiButton-outlined': {
                border: '1px solid rgba(0,0,0, 0.2)',
                backgroundColor: 'rgba(0,0,0, 0.01)'
              },
              '& .MuiButton-outlined:hover': {
                border: '1px solid rgba(0,0,0, 0.8)',
              },
              '& .MuiButton-root:hover': {
                backgroundColor: 'rgba(0,0,0, 0.05)',
              },
            },
          },
        ]
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none",
            color: theme.palette.text.primary,
            borderRadius: borderRadius_,
            fontSize: fontSize_,
            [theme.breakpoints.down('md')]: {
              padding: theme.spacing(1, 1.5),
            },
            [theme.breakpoints.up('md')]: {
              padding: theme.spacing(1, 2),
            },
            '& strong': {
              fontWeight: 600
            }
          }),
        },
        variants: [
          {
            props: { variant: "contained-light" },
            style: props => {
              return {
                background: props.theme.palette.grey[50]
              }
            }
          },
          {
            props: { variant: "checkout" },
            style: (props) => {
              const _colors = {
                success: {
                  // border: '2px solid',
                  // borderColor: props.theme.palette.success.main,
                  // ['.MuiSvgIcon-root']: {
                  //   fill: props.theme.palette.success.main,
                  // }
                  background: props.theme.palette.success.main,
                  color: props.theme.palette.common.white,
                  '&:hover': {
                    background: props.theme.palette.success.dark,
                  }
                }
              };
              return {
                ...(props.color ? _colors[props.color] : {}),
                height: '100%',
                borderRadius: 0,
                '& > .MuiStack-root': {
                  [props.theme.breakpoints.down('md')]: {
                    flexDirection: 'column',
                  }
                },
                '& > .MuiStack-root > .CheckoutButton-StartIcon .MuiSvgIcon-root': {
                  marginRight: props.theme.spacing(1),
                  marginLeft: props.theme.spacing(-0.5),
                },
                '& > .MuiStack-root > .CheckoutButton-EndIcon .MuiSvgIcon-root': {
                  marginRight: props.theme.spacing(-0.5),
                  marginLeft: props.theme.spacing(1),
                },
                '& > .MuiStack-root .MuiSvgIcon-root': {
                  fontSize: '2em',
                  [props.theme.breakpoints.down('md')]: {
                    margin: 'auto !important',
                  },
                  [props.theme.breakpoints.down('lg')]: {
                    fontSize: '1.5em',
                  }
                },
                '.text': {
                  fontWeight: 400
                },
                [props.theme.breakpoints.down('lg')]: {
                  '& .label, & .text': {
                    display: 'none',
                  },
                  [`&[data-keep-visible=${props['data-keep-visible']}] .${props['data-keep-visible']}`]: {
                    display: 'initial',
                    fontSize: '0.8em',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    lineHeight: 1.4,
                  }
                },
              };
            }
          },
          {
            props: { variant: "contained", color: "success" },
            style: ({ theme }) => ({
              color: 'white !important'
            })
          },
          {
            props: { variant: "contained-secondary" },
            style: ({ theme }) => ({
              backgroundColor: "#f0f0f0",
              color: theme.palette.getContrastText("#f0f0f0"),
            }),
          },
          {
            props: { variant: "contained-black" },
            style: ({ theme }) => ({
              backgroundColor: "#121212",
              color: theme.palette.getContrastText("#121212"),
              "&:hover": {
                backgroundColor: darken("#121212", 0.1),
              }
            }),
          },
          {
            props: { variant: "checkout-primary" },

            style: ({ theme }) => ({
              // fontSize: {
              //   xs: "1em",
              //   lg: "1.2em",
              // },
              backgroundColor: "#3db772",
              color: "#ffffff",
              paddingLeft: theme.spacing(2.25),
              paddingRight: theme.spacing(2.25),
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              "&:hover": {
                backgroundColor: darken("#3db772", 0.1),
              },
              "&:focus": {
                backgroundColor: darken("#3db772", 0.1),
              },
              "&:disabled": {
                backgroundColor: lighten("#3db772", 0.1),
              },
            }),
          },
        ],
      },
    },
  });

  console.log('THEME', theme);
  // window.__theme = theme;
  const cache = createCache({
    key: "mui",
    container: shadowRoot,
  });

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  );
};
